import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, debounceTime, first, map, takeUntil } from 'rxjs';
import { Boundries } from 'src/app/model/boundries.model';
import checkElementsOverlap from 'src/app/utils/checkElementsOverlap.util';
import { environment } from '../../environments/environment';
import { ConclusionType } from '../../model/conclusion-type.model';
import { ProcessType } from '../../model/process-type.model';
import { FormsService } from '../../services/forms.service';
import { ModalService, ModalType } from '../../services/modal.service';
import { ProcessService } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromAttachements from '../../store/attachments/attachments.reducer';
import * as fromProcess from '../../store/process/process.reducer';
import { selectProcessOfferVisible } from '../../store/process/process.reducer';
import { TrackingService } from '../../tracking/tracking.service';
import isAppAgentOrTravelAgency from '../../utils/is-agent-or-travel-agency.util';
import { GtmService } from '../../services/gtm.service';

const STEP_2 = 1;
const STEP_3 = 2;
const STEP_5 = 4;
const STEP_PAYMENTS = 6;
const STEP_CONCLUSION = 6;
const STEP_CONCLUSION_AGENT = 7;
const STEP_CONCLUSION_REQUEST = 8;

const NAV_BUTTON_MARGIN = 32;
const DELAY_TIME = 100;

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
})
export class NavigationComponent implements OnDestroy {
  appKind = environment.app;
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  conclusion$ = this.store.select(fromProcess.selectProcessConclusion);
  currentStep$ = this.store.select(fromProcess.selectProcessStep);
  selectKnfLimit$ = this.store.select(fromProcess.selectKnfLimit);
  nextStep$ = this.store.select(fromProcess.selectProcessNextStep);
  buttonLabel$ = this.nextStep$.pipe(map((step) => `common.next.${this.appKind}.${step}`));
  attachmentDownloaded$ = this.store.select(fromAttachements.selectAttachmentsIsAllDownloaded);
  nextStepVisible$ = this.store.select(fromProcess.selectProcessNextVisible);
  nextStepUrl$ = this.store.select(fromProcess.selectProcessNextStepUrl);
  formOfPayment$ = this.store.select(fromProcess.selectProcessFormOfPayment);
  displayConclusionNotice$ = combineLatest([this.currentStep$, this.nextStepUrl$]).pipe(
    map(([currentStep]) => this.isAppAgentOrTravelAgency && currentStep === STEP_CONCLUSION_AGENT),
  );
  disabled$ = combineLatest([
    this.processKind$,
    this.nextStep$,
    this.attachmentDownloaded$,
    this.formOfPayment$,
    this.selectKnfLimit$,
  ]).pipe(
    map(([processKind, nextStep, attachmentDownloaded, formOfPayment, knfLimit]) => {
      if (nextStep === STEP_2) {
        return processKind === null ? 'common.navigation-error.process-kind' : false;
      }

      if (nextStep === STEP_3) {
        switch (processKind) {
          case ProcessType.TRIPABROAD:
            this.formsService.tripAbroad.markAllAsTouched();

            return this.formsService.tripAbroad.invalid ? 'common.navigation-error.step-2-invalid' : false;
          case ProcessType.TRAVELINPL:
            this.formsService.travelInPl.markAllAsTouched();

            return this.formsService.travelInPl.invalid ? 'common.navigation-error.step-2-invalid' : false;
          case ProcessType.RESIGNATION:
            this.formsService.resignation.markAllAsTouched();

            return this.formsService.resignation.invalid ? 'common.navigation-error.step-2-invalid' : false;
        }
      }

      if (nextStep === STEP_5) {
        this.formsService.policyholder.markAllAsTouched();

        if (
          (this.isAppAgentOrTravelAgency && !knfLimit) ||
          this.formsService.policyholder.invalid ||
          this.formsService.agentCommissionSplits.invalid
        ) {
          return 'common.navigation-error.step-4-invalid';
        } else if (this.isAppAgentOrTravelAgency && knfLimit && knfLimit.result.isBlocked) {
          return knfLimit.result.noticeAboutBlock;
        }

        return false;
      }

      if (nextStep === STEP_PAYMENTS) {
        if (this.formsService.agreementsForm.invalid) {
          return 'common.navigation-error.checkbox-required-not-checked';
        }
        if (!this.isAppAgentOrTravelAgency && !attachmentDownloaded) {
          return 'common.navigation-error.attachments-not-downloaded';
        }
        this.gtmService.step7Event();

        return false;
      }

      if (nextStep === STEP_CONCLUSION_REQUEST) {
        if (this.isAppAgentOrTravelAgency && !formOfPayment) {
          return 'common.navigation-error.form-of-payment-not-selected';
        }

        return false;
      }

      return false;
    }),
  );
  offerValue$ = this.store.select(fromProcess.selectOfferValue);
  processOfferVisible$ = this.store.select(selectProcessOfferVisible);
  isOfferPriceBoxVisible$ = this.store.select(fromProcess.selectIsOfferPriceBoxVisible);
  navRowElement?: ElementRef<HTMLElement>;
  fabElement?: ElementRef<HTMLElement>;
  navButtonBoundries: Boundries | null = null;
  fabButtonBoundries: Boundries | null = null;
  buttonsOverlap = false;
  fabButtonBottomPosition: number | null = null;

  ngDestroy$ = new Subject<void>();

  modalType = ModalType;

  constructor(
    public processService: ProcessService,
    public modalService: ModalService,
    private store: Store<AppState>,
    private formsService: FormsService,
    private router: Router,
    public trackingService: TrackingService,
    public gtmService: GtmService,
  ) {}

  @HostListener('window:scroll', [])
  @HostListener('window:resize', [])
  onScrollOrResize() {
    this.setBoundries();
  }

  setBoundries(): void {
    if (this.navRowElement && this.fabElement) {
      this.navButtonBoundries = {
        top: this.navRowElement.nativeElement.getBoundingClientRect().top,
        bottom: this.navRowElement.nativeElement.getBoundingClientRect().bottom,
        right: this.navRowElement.nativeElement.getBoundingClientRect().right,
        left: this.navRowElement.nativeElement.getBoundingClientRect().left,
      };

      this.fabButtonBoundries = {
        top: this.fabElement.nativeElement.getBoundingClientRect().top,
        bottom: this.fabElement.nativeElement.getBoundingClientRect().bottom,
        right: this.fabElement.nativeElement.getBoundingClientRect().right,
        left: this.fabElement.nativeElement.getBoundingClientRect().left,
      };

      this.checkIfButtonsOverlap();
    }
  }

  checkIfButtonsOverlap() {
    if (this.navButtonBoundries && this.fabButtonBoundries && this.fabElement) {
      const navButtonBoundries = this.navButtonBoundries;
      let { top: navButtonBoundriesTop } = navButtonBoundries;
      const { bottom: navButtonBoundriesBottom } = navButtonBoundries;
      const { offsetHeight: fabButtonHeight } = this.fabElement.nativeElement;
      const { innerHeight: windowInnerHeight } = window;
      const removeOverlap =
        windowInnerHeight < navButtonBoundriesTop || windowInnerHeight > navButtonBoundriesBottom + NAV_BUTTON_MARGIN + fabButtonHeight;

      navButtonBoundriesTop = navButtonBoundriesTop - NAV_BUTTON_MARGIN;

      if (removeOverlap) {
        this.buttonsOverlap = false;

        return;
      }

      this.buttonsOverlap = this.buttonsOverlap ? true : checkElementsOverlap(navButtonBoundries, this.fabButtonBoundries);

      this.fabButtonBottomPosition = windowInnerHeight - navButtonBoundriesTop;
    }
  }

  updateElementsRef(element: ElementRef<HTMLElement>, type: string) {
    switch (type) {
      case 'navRow':
        this.navRowElement = element;
        break;
      case 'fab':
        this.fabElement = element;
        break;
      default:
        break;
    }

    if (this.navRowElement && this.fabElement) {
      this.setBoundries();

      this.currentStep$.pipe(takeUntil(this.ngDestroy$), debounceTime(DELAY_TIME)).subscribe(() => {
        this.buttonsOverlap = false;
        this.fabButtonBottomPosition = null;
        this.setBoundries();
      });
    }
  }

  goToNextStep() {
    this.trackingService.nextPage();

    combineLatest([this.disabled$, this.currentStep$, this.nextStepUrl$, this.conclusion$])
      .pipe(first())
      .subscribe(([disabled, currentStep, nextStepUrl, conclusion]) => {
        if (!conclusion && currentStep === STEP_CONCLUSION) {
          this.store.dispatch(fromProcess.actions.pushNavigationError({ error: 'common.navigation-error.conclusion-invalid' }));

          return;
        }

        if (disabled) {
          this.store.dispatch(fromProcess.actions.pushNavigationError({ error: disabled }));

          return;
        }

        if (currentStep === STEP_CONCLUSION && conclusion === ConclusionType.Remote) {
          this.modalService.openModal(ModalType.ConclusionRemote);

          return;
        }

        this.trackingImplementation(currentStep);

        void this.router.navigate(nextStepUrl);
      });
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  private trackingImplementation(step: number): void {
    switch (step) {
      case STEP_2:
        this.processKind$.pipe(first()).subscribe((processKind) => {
          if (!processKind) {
            console.error('processKind is null');

            return;
          }

          this.trackingImplementation2(processKind);
        });
        break;
    }
  }

  private trackingImplementation2(processKind: ProcessType): void {
    let destination: string | null = null;
    let departureDate: string | null = null;
    let returnDate: string | null = null;
    let peopleNumber: number | null = null;
    let allInPoland: boolean | null = null;
    let extremeSports: boolean | null = null;
    let phoneNumber: string | null = null;
    let travelersDOB: (number | null)[] | null = null;
    let extremeSportsEnabled: (boolean | null)[] | null = null;

    switch (processKind) {
      case ProcessType.TRIPABROAD: {
        const form = this.formsService.tripAbroad.value;

        destination = form.destination || null;
        departureDate = form.travelDates?.dateFrom || null;
        returnDate = form.travelDates?.dateTo || null;
        peopleNumber = form.numberOfInsureds || null;
        allInPoland = form.isPolandIsCurrentResiding || null;
        extremeSports = form.winterSportsHiRisk || null;
        phoneNumber = form.phoneNumber || null;
        travelersDOB = form.insuredBirthYear || null;
        extremeSportsEnabled = form.personsWithWinterSportsHiRisk?.map((value) => value || false) || null;

        break;
      }
      case ProcessType.TRAVELINPL: {
        const form = this.formsService.travelInPl.value;

        destination = null;
        departureDate = form.travelDates?.dateFrom || null;
        returnDate = form.travelDates?.dateTo || null;
        peopleNumber = form.numberOfInsureds || null;
        allInPoland = null;
        extremeSports = form.winterSportsHiRisk || null;
        phoneNumber = form.phoneNumber || null;
        travelersDOB = form.insuredBirthYear || null;
        extremeSportsEnabled = form.personsWithWinterSportsHiRisk?.map((value) => value || false) || null;

        break;
      }
      case ProcessType.RESIGNATION: {
        const form = this.formsService.resignation.value;
        destination = null;
        departureDate = form.travelDates?.dateFrom || null;
        returnDate = form.travelDates?.dateTo || null;
        peopleNumber = form.numberOfInsureds || null;
        allInPoland = null;
        extremeSports = null;
        phoneNumber = form.phoneNumber || null;
        travelersDOB = null;
        extremeSportsEnabled = null;
      }
    }

    this.trackingService.step2({
      destination,
      departureDate,
      returnDate,
      peopleNumber,
      allInPoland,
      extremeSports,
      phoneNumber,
      travelersDOB,
      extremeSportsEnabled,
    });
  }
}
